import React, { useState, useEffect } from 'react';
import { getCurrencySymbol } from '../utils/countryUtils';
import { useCountry } from '../contexts/CountryContext';

// Load saved user data from localStorage
const loadSavedUserData = () => {
  try {
    const saved = localStorage.getItem('parcelLookup_userData');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        firstName: parsed.firstName || '',
        lastName: parsed.lastName || '',
        phone: parsed.phone || '',
        email: parsed.email || '',
        countryCode: parsed.countryCode || null,
        wantsQuotation: false, // Reset checkbox
        acceptsTerms: false // Must accept again each time
      };
    }
  } catch (e) {
    console.error('Error loading saved user data:', e);
  }
  return {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    countryCode: null,
    wantsQuotation: false,
    acceptsTerms: false
  };
};

// Country phone codes
const COUNTRY_PHONE_CODES = [
  { code: '+51', country: 'PE', name: 'Perú', flag: '🇵🇪' },
  { code: '+52', country: 'MX', name: 'México', flag: '🇲🇽' },
  { code: '+56', country: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: '+57', country: 'CO', name: 'Colombia', flag: '🇨🇴' },
  { code: '+54', country: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: '+593', country: 'EC', name: 'Ecuador', flag: '🇪🇨' },
  { code: '+502', country: 'GT', name: 'Guatemala', flag: '🇬🇹' },
  { code: '+504', country: 'HN', name: 'Honduras', flag: '🇭🇳' },
  { code: '+503', country: 'SV', name: 'El Salvador', flag: '🇸🇻' },
  { code: '+506', country: 'CR', name: 'Costa Rica', flag: '🇨🇷' },
  { code: '+507', country: 'PA', name: 'Panamá', flag: '🇵🇦' },
  { code: '+505', country: 'NI', name: 'Nicaragua', flag: '🇳🇮' },
  { code: '+1-809', country: 'DO', name: 'República Dominicana', flag: '🇩🇴' },
  { code: '+1', country: 'US', name: 'Estados Unidos', flag: '🇺🇸' },
  { code: '+34', country: 'ES', name: 'España', flag: '🇪🇸' },
  { code: '+55', country: 'BR', name: 'Brasil', flag: '🇧🇷' },
  { code: '+58', country: 'VE', name: 'Venezuela', flag: '🇻🇪' },
  { code: '+595', country: 'PY', name: 'Paraguay', flag: '🇵🇾' },
  { code: '+598', country: 'UY', name: 'Uruguay', flag: '🇺🇾' },
  { code: '+591', country: 'BO', name: 'Bolivia', flag: '🇧🇴' },
];

// Helper: get dial code from ISO country code (e.g. 'GT' -> '+502')
const getDialCodeByCountry = (isoCode) => {
  if (!isoCode) return '+51';
  const found = COUNTRY_PHONE_CODES.find(c => c.country === isoCode.toUpperCase());
  return found ? found.code : '+51';
};

const WhatsAppModal = ({ isOpen, onClose, onSubmit, project, lotProperties, lotId, projectId, organizationPhone, organizationId, apiUrl }) => {

  const { country } = useCountry();
  const defaultDialCode = getDialCodeByCountry(country);

  const [formData, setFormData] = useState({
    ...loadSavedUserData(),
    countryCode: loadSavedUserData().countryCode || defaultDialCode
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isMessageSent, setIsMessageSent] = useState(false);

  // Load saved data when modal opens
  useEffect(() => {
    if (isOpen) {
      const savedData = loadSavedUserData();
      setFormData({
        ...savedData,
        countryCode: savedData.countryCode || defaultDialCode
      });
    }
  }, [isOpen, defaultDialCode]);


  // Generate WhatsApp message with current user data
  const generateWhatsAppMessage = () => {
    // Determinar si mostrar el precio basado en project.show_prices
    const shouldShowPrice = project?.show_prices && lotProperties?.price;
    const priceText = shouldShowPrice
      ? `${getCurrencySymbol(lotProperties?.currency || project?.currency)} ${lotProperties.price.toLocaleString()}`
      : 'Consultar';
    
    // Format phone number with country code for display
    const cleanPhone = formData.phone.replace(/[^0-9]/g, '');
    const fullPhoneNumber = `${formData.countryCode || defaultDialCode}${cleanPhone}`;
    
    // Construir nombre completo
    const fullName = [formData.firstName, formData.lastName].filter(Boolean).join(' ') || '___';
    
    let message = `Hola, mi nombre es ${fullName}.\n\n` +
      `Me interesa obtener más información sobre el lote ${lotProperties?.lot_number || lotProperties?.name || '-'} del proyecto ${project?.name || '-'}.\n\n` +
      `- Ubicación: ${project?.location || '-'}\n` +
      `- Lote: ${lotProperties?.lot_number || lotProperties?.name || '-'}\n` +
      `- Precio: ${priceText}\n` +
      `- Área: ${lotProperties?.area_calculated ? `${lotProperties.area_calculated.toFixed(2)} m²` : 'Consultar'}\n` +
      `- Mi contacto: ${fullPhoneNumber}\n\n`;
    
    if (formData.wantsQuotation) {
      message += `*SOLICITO COTIZACIÓN DE ESTE LOTE*\n\n`;
    }
    
    message += `Ver detalles: ${typeof window !== 'undefined' ? window.location.href : ''}\n\n` +
      `¿Podrían enviarme más información sobre este lote?`;
    
    return message;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es obligatorio';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es obligatorio';
    } else if (!/^[0-9\s()-]*$/.test(formData.phone)) {
      newErrors.phone = 'Ingrese un teléfono válido (solo números)';
    }
    
    // Validar email si se ingresó
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ingrese un email válido';
    }
    
    // Validar consentimientos requeridos
    if (!formData.acceptsTerms) {
      newErrors.acceptsTerms = '⚠️ Es necesario aceptar los términos y condiciones para continuar';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Generate final WhatsApp message with user data
      const whatsappMessage = generateWhatsAppMessage();

      // Clean phone number for backend (remove all non-numeric characters)
      const cleanPhoneForBackend = formData.phone.replace(/[^0-9]/g, '');

      // Send data to backend
      // Note: organization_id is obtained from the project in the backend, not sent here
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName || null,
        identification_number: null,
        phone: cleanPhoneForBackend, // Send clean phone without country code to backend
        email: formData.email || null,
        lot_id: lotId,
        project_id: projectId,
        message: null,  // Don't save the message in the database, it's only for WhatsApp
        wants_quotation: formData.wantsQuotation || false,
        accepts_terms: formData.acceptsTerms,
        accepts_privacy: true,  // Automatically accepted
        consents_to_data_use: true  // Automatically accepted
      };
      
      if (apiUrl) {
        const response = await fetch(`${apiUrl}/notifications`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json', 
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          console.error('Error al guardar en backend:', await response.text());
          // Continue anyway to open WhatsApp
        }
      }
      
      // Show redirecting message
      setIsSubmitting(false);
      setIsRedirecting(true);
      
      // Wait a moment to show the redirecting message
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Format phone number with country code
      const cleanPhone = formData.phone.replace(/[^0-9]/g, '');
      const fullPhoneNumber = `${formData.countryCode || defaultDialCode}${cleanPhone}`;
      
      // Send to WhatsApp with the final message and formatted phone
      onSubmit({ ...formData, phone: fullPhoneNumber, message: whatsappMessage });
      
      // Show success message
      setIsRedirecting(false);
      setIsMessageSent(true);
      
      // Save user data to localStorage (without country code in phone, keep it separate)
      try {
        localStorage.setItem('parcelLookup_userData', JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: cleanPhone,
          email: formData.email,
          countryCode: formData.countryCode || defaultDialCode
        }));
      } catch (e) {
        console.error('Error saving user data:', e);
      }
      
      // Reset form state (keep saved data for next time)
      setFormData({ 
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: cleanPhone, 
        email: formData.email,
        countryCode: formData.countryCode || defaultDialCode,
        wantsQuotation: false,
        acceptsTerms: false
      });
    } catch (error) {
      console.error('Error al enviar datos:', error);
      // Continue anyway to open WhatsApp
      setIsSubmitting(false);
      setIsRedirecting(true);
      
      // Wait a moment to show the redirecting message
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const whatsappMessage = generateWhatsAppMessage();
      
      // Format phone number with country code
      const cleanPhone = formData.phone.replace(/[^0-9]/g, '');
      const fullPhoneNumber = `${formData.countryCode || defaultDialCode}${cleanPhone}`;
      
      onSubmit({ ...formData, phone: fullPhoneNumber, message: whatsappMessage });
      
      // Show success message
      setIsRedirecting(false);
      setIsMessageSent(true);
      
      // Save user data to localStorage (without country code in phone, keep it separate)
      try {
        localStorage.setItem('parcelLookup_userData', JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: cleanPhone,
          email: formData.email,
          countryCode: formData.countryCode || defaultDialCode
        }));
      } catch (e) {
        console.error('Error saving user data:', e);
      }
      
      // Reset form state
      setFormData({ 
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: cleanPhone, 
        email: formData.email,
        countryCode: formData.countryCode || defaultDialCode,
        wantsQuotation: false,
        acceptsTerms: false
      });
    } finally {
      setIsSubmitting(false);
      setIsRedirecting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCloseAfterSuccess = () => {
    setIsMessageSent(false);
    onClose();
  };

  const handleClearSavedData = () => {
    if (window.confirm('¿Deseas eliminar los datos guardados? Deberás completar el formulario nuevamente.')) {
      try {
        localStorage.removeItem('parcelLookup_userData');
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          countryCode: defaultDialCode,
          wantsQuotation: false,
          acceptsTerms: false
        });
      } catch (e) {
        console.error('Error clearing saved data:', e);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Cerrar solo si se hace clic en el overlay, no en el contenido del modal
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-green-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isMessageSent ? '¡Mensaje enviado!' : 'Contactar por WhatsApp'}
              </h3>
              <p className="text-sm text-gray-600">
                {isMessageSent ? 'Redirigido a WhatsApp' : 'Completa tus datos para contactarnos'}
              </p>
            </div>
          </div>
          {!isMessageSent && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Success Message */}
        {isMessageSent ? (
          <div className="p-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">¡Mensaje enviado con éxito!</h3>
                <p className="text-gray-600 text-sm">
                  Tu información ha sido procesada y se abrió WhatsApp para que puedas enviar el mensaje.
                </p>
              </div>
              <button
                onClick={handleCloseAfterSuccess}
                className="mt-4 px-6 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Form */}
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {/* Nombre */}
          <div>
            <label className="flex items-center space-x-2 text-xs font-semibold text-gray-700 mb-1">
              <span>Nombre <span className="text-red-500">*</span></span>
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Ingresa tu nombre"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm ${
                errors.firstName 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-green-500'
              }`}
            />
            {errors.firstName && (
              <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
            )}
          </div>

          {/* Apellidos */}
          <div>
            <label className="flex items-center space-x-2 text-xs font-semibold text-gray-700 mb-1">
              <span>Apellido</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName || ''}
              onChange={handleChange}
              placeholder="Ingresa tu apellido"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm ${
                errors.lastName 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-green-500'
              }`}
            />
            {errors.lastName && (
              <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
            )}
          </div>

          {/* Teléfono con código de país */}
          <div>
            <label className="flex items-center space-x-2 text-xs font-semibold text-gray-700 mb-1">
              <span>Teléfono <span className="text-red-500">*</span></span>
            </label>
            <div className="flex gap-2">
              {/* Dropdown de código de país */}
              <select
                name="countryCode"
                value={formData.countryCode || defaultDialCode}
                onChange={handleChange}
                className="px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors text-sm bg-white"
              >
                {COUNTRY_PHONE_CODES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.code}
                  </option>
                ))}
              </select>
              {/* Input de número de teléfono */}
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="999 999 999"
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm ${
                  errors.phone 
                    ? 'border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-green-500'
                }`}
              />
            </div>
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>

          {/* DNI */}
          {/* <div>
            <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
              </svg>
              <span>DNI</span>
            </label>
            <input
              type="text"
              name="identificationNumber"
              value={formData.identificationNumber}
              onChange={handleChange}
              placeholder="Ingresa tu DNI (8 dígitos)"
              maxLength="8"
              pattern="[0-9]*"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                errors.identificationNumber 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-green-500'
              }`}
            />
            {errors.identificationNumber && (
              <p className="text-red-500 text-xs mt-1">{errors.identificationNumber}</p>
            )}
          </div> */}

          {/* Email */}
          <div>
            <label className="flex items-center space-x-2 text-xs font-semibold text-gray-700 mb-1">
              <span>Email (opcional)</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tuemail@ejemplo.com"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm ${
                errors.email 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-green-500'
              }`}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Info banner about saved data */}
          <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-200 text-xs">
            <span className="text-green-700 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Tus datos se guardan automáticamente
            </span>
            <button
              type="button"
              onClick={handleClearSavedData}
              className="text-green-600 hover:text-green-800 underline text-xs"
            >
              Limpiar
            </button>
          </div>

          {/* Checkbox para cotización */}
          <div 
            className="flex items-center space-x-2 p-3 bg-brand-50 rounded-lg border border-brand-200"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              name="wantsQuotation"
              checked={formData.wantsQuotation}
              onChange={handleChange}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
            />
            <label 
              className="text-sm font-medium text-gray-700 cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              📄 Deseo recibir cotización de este lote
            </label>
          </div>

          {/* Consentimientos legales */}
          <div 
            className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                name="acceptsTerms"
                checked={formData.acceptsTerms}
                onChange={handleChange}
                onClick={(e) => e.stopPropagation()}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-0.5"
              />
              <label 
                className="text-xs text-gray-700 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                ✅ Acepto los <a href="#" target="_blank" className="text-brand-600 hover:underline" onClick={(e) => e.stopPropagation()}>Términos y Condiciones de Uso</a> y las <a href="#" target="_blank" className="text-brand-600 hover:underline" onClick={(e) => e.stopPropagation()}>políticas de privacidad</a>
                {errors.acceptsTerms && <span className="text-red-500 text-xs block">{errors.acceptsTerms}</span>}
              </label>
            </div>
          </div>

          {/* Botones */}
          <div className="flex space-x-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isRedirecting}
              className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm">Procesando información...</span>
                </>
              ) : isRedirecting ? (
                <>
                  <svg className="animate-pulse h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <span className="text-sm">Redirigiendo a WhatsApp...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span className="text-sm">Enviar</span>
                </>
              )}
            </button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
};

export default WhatsAppModal;

